// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api.js';
import { saveSession } from '../utils/auth.js';
import { supabase } from '../supabaseClient';
import Logo from '../components/shared/Logo.jsx';

export default function Login() {
  const navigate = useNavigate();
  const emailRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [resetSent, setResetSent] = useState(false);
  const [emailHint, setEmailHint] = useState('');

  const params = new URLSearchParams(window.location.search);
  const googleAuthFailed = params.get('error') === 'auth_failed';

  useEffect(() => {
    const emailParam = params.get('email');
    if (emailParam) setEmail(decodeURIComponent(emailParam));
  }, []);

  async function handleGoogleSignIn() {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/auth/callback' },
    });
    if (oauthError) setError('Something went wrong. Please try again.');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setEmailHint('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setError('Please enter a valid email address.');
    }
    if (!password) {
      return setError('Please enter your password.');
    }

    setLoading(true);
    try {
      const data = await api.login(email, password);
      saveSession(data.session, data.user);
      navigate('/dashboard');
    } catch {
      const next = failCount + 1;
      setFailCount(next);
      if (next >= 3) {
        setError('__signup_hint__');
      } else {
        setError('Incorrect email or password.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setEmailHint('');
    if (!email.trim()) {
      emailRef.current?.focus();
      setEmailHint('Enter your email above first.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setResetSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid var(--color-border)',
    borderRadius: 10,
    fontSize: 14,
    color: 'var(--color-text)',
    background: 'var(--color-surface)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: 6,
    display: 'block',
  };

  if (resetSent) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={36} />
          </div>

          <div style={{
            width: 48, height: 48,
            borderRadius: '50%',
            background: 'var(--color-green-bg)',
            border: '1px solid var(--color-green-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10l4.5 4.5 7.5-8" stroke="var(--color-green-text)"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', textAlign: 'center', marginBottom: 12, letterSpacing: '-0.02em' }}>
            Check your email
          </h2>
          <p style={{ fontSize: 14, color: 'var(--color-text-2)', textAlign: 'center', marginBottom: 4 }}>
            We sent a password reset link to:
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', textAlign: 'center', marginBottom: 20 }}>
            {email}
          </p>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => { setResetSent(false); setError(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-primary)', fontWeight: 500, padding: 0 }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <Logo size={36} />
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', textAlign: 'center', letterSpacing: '-0.02em', marginBottom: 4 }}>
          Welcome back
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-2)', textAlign: 'center', marginBottom: 28 }}>
          Sign in to your CatalogConvert account.
        </p>

        <button
          onClick={handleGoogleSignIn}
          style={{
            width: '100%',
            padding: '11px 16px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 10,
            background: 'var(--color-surface)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--color-text)',
            transition: 'background 0.15s, border-color 0.15s',
            marginBottom: googleAuthFailed ? 8 : 20,
            boxSizing: 'border-box',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--color-surface)'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908C16.658 14.205 17.64 11.892 17.64 9.2z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.576c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.576 9 3.576z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {googleAuthFailed && (
          <p style={{ fontSize: 12, color: 'var(--color-red-text)', margin: '0 0 20px 0' }}>
            Google sign-in failed. Please try again or use email below.
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
          <span style={{ fontSize: 12, color: 'var(--color-text-3)', whiteSpace: 'nowrap' }}>or continue with email</span>
          <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <label style={labelStyle} htmlFor="login-email">Email address</label>
          <input
            id="login-email"
            ref={emailRef}
            type="email"
            placeholder="you@yourstore.com"
            autoFocus
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); setEmailHint(''); }}
            style={{ ...inputStyle, marginBottom: emailHint ? 4 : 16 }}
            onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--color-border)'}
          />
          {emailHint && (
            <p style={{ fontSize: 12, color: 'var(--color-text-3)', marginBottom: 12, marginTop: 0 }}>
              {emailHint}
            </p>
          )}

          <label style={labelStyle} htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            placeholder="Your password"
            autoComplete="current-password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            style={{ ...inputStyle, marginBottom: 6 }}
            onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--color-border)'}
          />

          <div style={{ textAlign: 'right', marginBottom: 20 }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-primary)', fontWeight: 500, padding: 0 }}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: loading ? 'var(--color-text-3)' : 'var(--color-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? 'default' : 'pointer',
              letterSpacing: '-0.01em',
              transition: 'background 0.15s',
              marginBottom: 20,
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary-hover)'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary)'; }}
          >
            {loading ? 'Signing in...' : 'Sign in →'}
          </button>
        </form>

        {error === '__signup_hint__' ? (
          <p style={{ fontSize: 12, color: 'var(--color-red-text)', textAlign: 'center', marginBottom: 16, marginTop: -8 }}>
            Incorrect email or password.{' '}
            Don't have an account yet?{' '}
            <Link to="/signup" style={{ color: 'var(--color-red-text)', fontWeight: 600, textDecoration: 'underline' }}>
              Create one free
            </Link>
          </p>
        ) : error ? (
          <p style={{ fontSize: 12, color: 'var(--color-red-text)', textAlign: 'center', marginBottom: 16, marginTop: -8 }}>
            {error}
          </p>
        ) : null}

        <p style={{ fontSize: 13, color: 'var(--color-text-3)', textAlign: 'center', marginBottom: 24 }}>
          Don't have an account?{' '}
          <Link
            to="/signup"
            style={{ color: 'var(--color-primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
          >
            Create one free
          </Link>
        </p>

        <p style={{ fontSize: 11, color: 'var(--color-text-3)', textAlign: 'center', margin: 0 }}>
          By continuing, you agree to our{' '}
          <a href="/terms" style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            Terms of Service
          </a>
          {' '}and{' '}
          <a href="/privacy" style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            Privacy Policy
          </a>.
        </p>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-bg)',
  padding: 24,
};

const cardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  boxShadow: 'var(--shadow-card)',
  padding: '40px 40px 32px',
  width: '100%',
  maxWidth: 440,
  boxSizing: 'border-box',
};
